/* eslint-disable global-require */
import { ErrorBoundary } from 'react-error-boundary';

import * as amplitude from '@amplitude/analytics-browser';
import { BulbTwoTone, CheckCircleOutlined, InfoCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';

import { Alert, DatePicker, Modal } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import GGButton from 'components/buttons/GGButton';
import CalendarTabs from 'components/cards/CalendarTabs';
import CalendarWeekBar from 'components/cards/CalendarWeek';
import ModuleCard from 'components/cards/ModuleCard';
import NextPeriodPrediction from 'components/cards/NextPeriodPrediction';
import CompareCycles from 'components/charts/CompareCycles';
import LinearCalendarChart from 'components/charts/LinearCalendarChart';
import PastCyclesLength from 'components/charts/PastCyclesLength';
import PeriodChart from 'components/charts/PeriodChart';
import SymptomsChart from 'components/charts/SymptomsChart';
import SymptomsChartLegend from 'components/charts/SymptomsChartLegend';
import PageTopBar from 'components/navigation/PageTopBar';
import SymptomTrackingReminder from 'components/SymptomTrackingReminder';
import AppColors from 'config/AppColors';
import dayjs from 'dayjs';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import usePreferences from 'hooks/usePreferences';
import useReminderState from 'hooks/useReminderState';
import useSchedulePeriodNotifications from 'hooks/useSchedulePeriodNotifications';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import { locale } from 'locales/i18nManager';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import Platform from 'utils/Platform';

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: AppColors.backgroundGrey,
  },
  container: {
    padding: '8px 12px 32px',
    overflowY: 'scroll',
    flexGrow: 1,
    maxWidth: 600,
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 32,
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: 24,
    position: 'relative',
  },
  errorFallback: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    textAlign: 'center',
    color: AppColors.colorMap.bad,
    padding: '0px 32px',
  },
  standaloneReminderContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  reminderContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
  },
  periodReminderSuggestionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: 12,
  },
  helperIcon: {
    cursor: 'pointer',
    color: AppColors.text.veryLightGray,
    marginTop: 8,
  },
  historyContainer: {
    marginTop: 12,
    position: 'relative',
  },
  historyRangeFooter: {
    maxWidth: 264,
    lineHeight: '1.5em',
    '& svg': {
      marginRight: 4,
    },
  },
  historyGraphTitlesContainer: {
    // this container is overlaid with the history graphs container
    display: 'flex',
    flexDirection: 'column',
    gap: 85, // graph height + gap
    '& > h3': {
      margin: '8px 0px 0px',
      height: 24,
    },
  },
  historyGraphsContainer: {
    // this container is overlaid on top of the history titles container
    // it allows smooth scrolling of all graphs together
    overflowX: 'auto',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 24 + 8, // title height + gap
    paddingTop: 24 + 8, // first title height + gap
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  graphTitle: {
    margin: '16px 0px 8px',
    color: AppColors.text.discrete,
  },
  noPeriodsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,

    '& button': {
      padding: 0,
    },

    '& p': {
      margin: 0,
    },
  },
  emptySpaceBehindSurveyButton: {
    height: 64,
    minHeight: 64,
  },
  fillSurveyButton: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    padding: 32,
    background: `linear-gradient(to top, ${AppColors.backgroundGrey}, ${AppColors.backgroundGrey} 50%, ${AppColors.backgroundGrey}00 100%);`,
  },
  historyGraphsFakeContainer: {
    overflow: 'hidden',
  },
  pleaseFillYourDataOverlay: {
    color: AppColors.text.veryLightGray,
    fontSize: 14,
    textAlign: 'center',
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    backdropFilter: 'blur(2px)',

    '& p': {
      margin: 0,
    },
  },
  pleaseFillYourDataOverlayText: {
    background: `radial-gradient(closest-side, rgba(255,255,255,1), rgba(255,255,255,0))`,
    textAlign: 'center',
    padding: '48px 36px',
    borderRadius: 8,
    color: AppColors.colorTextBase,
  },
});

export default function SymptomTracking() {
  // Hooks
  const styles = useStyles();
  const [api, context] = useNotification();
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const historyGraphsRef = useRef<HTMLDivElement>(null);
  const [historyEndDate, setHistoryEndDate] = useState<dayjs.Dayjs>(dayjs());
  const historyStartDate = historyEndDate.subtract(3, 'month');
  const navigate = useNavigate();

  const { doesUserHavePushNotifications } = useCheckNotificationsPermissions();

  // period tracking preferences
  const { value: shouldAskForPeriodPreference, set: storeShouldAskForPeriod } = usePreferences(
    Keys.TRACKING_SURVEY_SHOULD_ASK_FOR_PERIOD,
  );
  const isPeriodTrackingEnabled = shouldAskForPeriodPreference !== false;

  // reminder state (managed via both preferences and Redux store)
  const {
    isPeriodReminderEnabled,
    areDailyNotificationsEnabled,
    setPeriodReminderEnabled,
    clearPeriodReminderEnabled,
  } = useReminderState();

  const isAnyNotificationEnabled = areDailyNotificationsEnabled || isPeriodReminderEnabled;
  const { schedulePeriodReminder } = useSchedulePeriodNotifications();

  const {
    set: setDidWeAlreadySuggestPeriodReminder,
    value: didWeAlreadySuggestPeriodReminder,
    loading: loadingDidWeAlreadySuggestPeriodReminder,
  } = usePreferences(Keys.TRACKING_DID_WE_ALREADY_SUGGEST_PERIOD_REMINDER);

  const areNotificationsBlocked = isAnyNotificationEnabled && !doesUserHavePushNotifications;

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarMode, setCalendarMode] = useState<'calendar' | 'periodFilling'>('calendar');
  const [showPeriodReminderSuggestion, setShowPeriodReminderSuggestion] = useState(false);

  // Helper modal states
  const [showCycleLengthHelp, setShowCycleLengthHelp] = useState(false);
  const [showCompareCyclesHelp, setShowCompareCyclesHelp] = useState(false);

  const shouldShowPeriodReminderSuggestion =
    Platform.isNative() &&
    ((showPeriodReminderSuggestion &&
      !loadingDidWeAlreadySuggestPeriodReminder &&
      !(didWeAlreadySuggestPeriodReminder === true)) ||
      areNotificationsBlocked);

  const [periodReminderPage, setPeriodReminderPage] = useState<
    'ask' | 'confirmNo' | 'confirmYes' | 'notificationsDisabled'
  >('ask');

  const {
    getSpecificDailyStatus,
    getStatusValueForDisplayedDays,
    refresh,
    hasTrackingForToday,
    probableNextPeriodDates,
    hasDataOtherThanPeriod,
    dataOtherThanPeriod,
  } = useSymptomTrackingData();

  const today = dayjs();
  const displayedDays = useMemo(() => {
    return Array.from({ length: historyEndDate.diff(historyStartDate, 'day') + 1 }, (_, i) =>
      historyStartDate.add(i, 'day').set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0),
    );
  }, [historyStartDate, historyEndDate]);

  const displayBottomCallToAction = hasDataOtherThanPeriod && !hasTrackingForToday;
  const enoughDataForHistory = dataOtherThanPeriod.daysWithData?.length && dataOtherThanPeriod.daysWithData.length > 2;

  useEffect(() => {
    // sometimes in functional tests, I think useRequest considers two very close requests as the same
    // and does not refresh the data when this page mounts. This fixes it
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (areNotificationsBlocked) {
      setPeriodReminderPage('notificationsDisabled');
    }
  }, [areNotificationsBlocked]);

  useEffect(() => {
    if (historyGraphsRef.current) {
      historyGraphsRef.current.scrollLeft = historyGraphsRef.current.scrollWidth;
    }
  }, [historyGraphsRef.current, enoughDataForHistory]);

  const onFillCycles = () => {
    setIsCalendarOpen(true);
    setCalendarMode('periodFilling');
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    posthog.capture('tracking.period_quickfill_started');
  };
  const onFillPastSymptoms = () => {
    setIsCalendarOpen(true);
    setCalendarMode('calendar');
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onValidateFillingFirstPeriods = () => {
    setCalendarMode('calendar');
    setIsCalendarOpen(false);
    if (probableNextPeriodDates) {
      setShowPeriodReminderSuggestion(true);
      setPeriodReminderPage('ask');
    }
    posthog.capture('tracking.period_quickfill_validated');
  };

  const enablePeriodReminder = useCallback(() => {
    setPeriodReminderEnabled(true);
    setPeriodReminderPage('confirmYes');
    if (probableNextPeriodDates) {
      schedulePeriodReminder(probableNextPeriodDates.start);
    }
    posthog.capture('tracking.period_reminder.enabled', { source: 'suggester' });
  }, [setPeriodReminderEnabled, probableNextPeriodDates, schedulePeriodReminder]);

  const refusePeriodReminder = () => {
    setPeriodReminderPage('confirmNo');
    posthog.capture('tracking.period_reminder.refused');
  };

  useEffect(() => {
    // when coming back from notification settings, if notifications have been enabled,
    // enable the reminder and switch to the confirmation page
    if (doesUserHavePushNotifications && periodReminderPage === 'notificationsDisabled') {
      enablePeriodReminder();
    }
  }, [doesUserHavePushNotifications, periodReminderPage, enablePeriodReminder]);

  const setIsCalendarOpenWithScrollHandler = (newIsCalendarOpen: boolean) => {
    setIsCalendarOpen(newIsCalendarOpen);
    if (newIsCalendarOpen) {
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
      <div className={styles.errorFallback}>
        <p>{t('symptomTracking.error.title')}</p>
      </div>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <PageTopBar title={t('symptomTracking.title')} />
      <div className={styles.container} ref={containerRef}>
        {context}
        <CalendarWeekBar
          isCalendarOpen={isCalendarOpen}
          setIsCalendarOpen={setIsCalendarOpenWithScrollHandler}
          calendarMode={calendarMode}
          setCalendarMode={setCalendarMode}
          onValidateFillingFirstPeriods={onValidateFillingFirstPeriods}
        />
        {isPeriodTrackingEnabled && (
          <div className={styles.headerContainer}>
            <NextPeriodPrediction />
            <div className={styles.reminderContainer}>
              <SymptomTrackingReminder iconOnly />
            </div>
          </div>
        )}
        {!isPeriodTrackingEnabled && (
          <div className={styles.standaloneReminderContainer}>
            <SymptomTrackingReminder />
          </div>
        )}
        {shouldShowPeriodReminderSuggestion && (
          <Alert
            type={periodReminderPage === 'notificationsDisabled' ? 'warning' : 'info'}
            style={{ width: '100%' }}
            message={
              <div className={styles.periodReminderSuggestionContainer}>
                {periodReminderPage === 'ask' && (
                  <>
                    <p>{t('symptomTracking.periodReminder.question')}</p>
                    <div>
                      <GGButton color="danger" type="link" onClick={refusePeriodReminder}>
                        {t('symptomTracking.periodReminder.later')}
                      </GGButton>
                      <GGButton
                        type="primary"
                        onClick={() => {
                          if (doesUserHavePushNotifications) {
                            enablePeriodReminder();
                          } else {
                            setPeriodReminderPage('notificationsDisabled');
                          }
                        }}
                      >
                        {t('symptomTracking.periodReminder.yes')}
                      </GGButton>
                    </div>
                  </>
                )}
                {periodReminderPage === 'confirmNo' && (
                  <>
                    <p>{t('symptomTracking.periodReminder.laterExplanation')}</p>
                    <div>
                      <GGButton
                        type="primary"
                        onClick={() => {
                          setShowPeriodReminderSuggestion(false);
                          setDidWeAlreadySuggestPeriodReminder('true');
                        }}
                      >
                        {t('symptomTracking.periodReminder.ok')}
                      </GGButton>
                    </div>
                  </>
                )}
                {periodReminderPage === 'confirmYes' && (
                  <>
                    <p>
                      <CheckCircleOutlined style={{ color: AppColors.main, marginRight: 8 }} />{' '}
                      {t('symptomTracking.periodReminder.enabled')}
                    </p>
                    <div>
                      <GGButton
                        type="primary"
                        onClick={() => {
                          setShowPeriodReminderSuggestion(false);
                          setDidWeAlreadySuggestPeriodReminder('true');
                        }}
                      >
                        {t('symptomTracking.periodReminder.ok')}
                      </GGButton>
                    </div>
                  </>
                )}
                {periodReminderPage === 'notificationsDisabled' && (
                  <>
                    <p>{t('symptomTracking.reminder.enableNotifications')}</p>
                    <div>
                      <GGButton
                        type="primary"
                        onClick={() => {
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          NativeSettings.open({
                            optionAndroid: AndroidSettings.AppNotification,
                            optionIOS: IOSSettings.App,
                          });
                          posthog.capture('notification_suggester_clicked');
                        }}
                      >
                        {t('symptomTracking.periodReminder.enable')}
                      </GGButton>
                    </div>
                  </>
                )}
              </div>
            }
          />
        )}
        {isPeriodTrackingEnabled && (
          <ModuleCard
            title={t('symptomTracking.modules.cycleLengthTitle')}
            topRightContent={
              <QuestionCircleOutlined
                onClick={() => {
                  setShowCycleLengthHelp(true);
                  posthog.capture('tracking.cycle_length_help_opened');
                }}
                className={styles.helperIcon}
              />
            }
          >
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error, errorInfo) => console.error('Error in PastCyclesLength', error, errorInfo)}
            >
              <PastCyclesLength
                onFillCycles={onFillCycles}
                onNoPeriods={() => {
                  storeShouldAskForPeriod('false');
                  posthog.capture('tracking.period_tracking_disabled');
                }}
              />
            </ErrorBoundary>
          </ModuleCard>
        )}
        {isPeriodTrackingEnabled && (
          <ModuleCard
            title={t('symptomTracking.modules.compareCyclesTitle')}
            topRightContent={
              <QuestionCircleOutlined
                onClick={() => {
                  setShowCompareCyclesHelp(true);
                  posthog.capture('tracking.compare_cycles_help_opened');
                }}
                className={styles.helperIcon}
              />
            }
          >
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error, errorInfo) => console.error('Error in CompareCycles', error, errorInfo)}
            >
              <CompareCycles onFillPastSymptomsClicked={onFillPastSymptoms} />
            </ErrorBoundary>
          </ModuleCard>
        )}
        <ModuleCard
          title={t('symptomTracking.modules.historyTitle')}
          topRightContent={
            enoughDataForHistory ? (
              <DatePicker
                allowClear={false}
                value={historyEndDate}
                variant="filled"
                onChange={value => {
                  setHistoryEndDate(value);
                  posthog.capture('tracking.history_range_changed');
                }}
                format={value =>
                  `${historyStartDate.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })} - ${value.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`
                }
                renderExtraFooter={() => {
                  return (
                    <p className={styles.historyRangeFooter}>
                      <InfoCircleFilled /> {t('symptomTracking.history.historyDescription')}
                    </p>
                  );
                }}
              />
            ) : null
          }
        >
          <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={(error, errorInfo) => console.error('Error in History', error, errorInfo)}
          >
            <div className={styles.historyContainer}>
              {enoughDataForHistory ? (
                <>
                  <div className={styles.historyGraphTitlesContainer}>
                    <h3 className={styles.graphTitle}>{t('symptomTracking.graphs.generalState')}</h3>
                    {isPeriodTrackingEnabled && (
                      <h3 className={styles.graphTitle}>{t('symptomTracking.graphs.menstruation')}</h3>
                    )}
                    <h3 className={styles.graphTitle}>{t('symptomTracking.graphs.pain')}</h3>
                    <h3 className={styles.graphTitle}>{t('symptomTracking.graphs.symptoms')}</h3>
                    <SymptomsChartLegend dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])} />
                  </div>
                  <div className={styles.historyGraphsContainer} ref={historyGraphsRef}>
                    <LinearCalendarChart
                      dayLabels={displayedDays}
                      dayValues={getStatusValueForDisplayedDays('general_state', displayedDays)}
                      range={[0, 4]}
                      showMonthLabels
                    />
                    {isPeriodTrackingEnabled && (
                      <PeriodChart
                        dayLabels={displayedDays}
                        dayValues={getStatusValueForDisplayedDays('menstruation_amount', displayedDays)}
                        showMonthLabels
                      />
                    )}
                    <LinearCalendarChart
                      dayLabels={displayedDays}
                      dayValues={getStatusValueForDisplayedDays('pain_level', displayedDays)}
                      range={[0, 4]}
                      reverseColors
                      showMonthLabels
                    />
                    <SymptomsChart
                      dayLabels={displayedDays}
                      dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])}
                      showMonthLabels
                    />
                  </div>
                  <h3 className={styles.graphTitle}>{t('symptomTracking.graphs.notes')}</h3>
                  <CalendarTabs
                    dailyStatuses={displayedDays.map(getSpecificDailyStatus).filter(d => d !== undefined)}
                  />
                </>
              ) : (
                <div className={styles.historyGraphsFakeContainer}>
                  <div style={{ position: 'relative' }}>
                    <LinearCalendarChart
                      dayLabels={Array.from({ length: 40 }, (_, i) => today.subtract(i, 'days')).reverse()}
                      dayValues={Array(2).fill([0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 1, 0, 0, 1, 0, 0, 0]).flat()}
                      range={[0, 4]}
                      reverseColors
                      showMonthLabels
                    />
                    <LinearCalendarChart
                      dayLabels={Array.from({ length: 40 }, (_, i) => today.subtract(i, 'days')).reverse()}
                      dayValues={Array(2).fill([4, 3, 4, 3, 4, 4, 4, 4, 4, 3, 1, 2, 1, 3, 4, 4, 4, 4, 4, 3]).flat()}
                      range={[0, 4]}
                      showMonthLabels
                    />
                    <div className={styles.pleaseFillYourDataOverlay}>
                      <div className={styles.pleaseFillYourDataOverlayText}>
                        <p style={{ fontSize: 24 }}>
                          <BulbTwoTone style={{ color: '#FFF5D6' }} twoToneColor="#FFD147" />
                        </p>
                        <p>{t('symptomTracking.history.fillAtLeast3Days')}</p>
                      </div>
                    </div>
                  </div>
                  <GGButton block size="large" type="link" onClick={onFillPastSymptoms}>
                    {t('symptomTracking.history.fillPastSymptoms')}
                  </GGButton>
                </div>
              )}
            </div>
          </ErrorBoundary>
        </ModuleCard>
        {!isPeriodTrackingEnabled && (
          <ModuleCard title={t('symptomTracking.graphs.menstruation')}>
            <div className={styles.noPeriodsContainer}>
              <p>{t('symptomTracking.tracking.disabled')}</p>
              <GGButton
                type="link"
                onClick={() => {
                  storeShouldAskForPeriod('true');
                  posthog.capture('tracking.period_tracking_reactivated');
                }}
              >
                {t('symptomTracking.tracking.reactivate')}
              </GGButton>
            </div>
          </ModuleCard>
        )}
        {displayBottomCallToAction && <div className={styles.emptySpaceBehindSurveyButton} />}
      </div>
      {displayBottomCallToAction && (
        <div className={styles.fillSurveyButton}>
          <GGButton
            type="primary"
            size="large"
            block
            onClick={() => {
              navigate(routes.symptomTrackingSurvey.route);
              amplitude.track('tracking.survey.started', {
                source: 'daily_button',
              });
              posthog.capture('tracking.survey.started', {
                source: 'daily_button',
              });
            }}
          >
            {t('symptomTracking.actions.fillDailySurvey')}
          </GGButton>
        </div>
      )}

      {/* Helper Modals */}
      <Modal
        open={showCycleLengthHelp}
        onCancel={() => setShowCycleLengthHelp(false)}
        footer={null}
        title={t('symptomTracking.modules.cycleLengthHelp.title')}
      >
        <p>
          <Trans i18nKey="symptomTracking.modules.cycleLengthHelp.description" components={{ br: <br /> }} />
        </p>
      </Modal>

      <Modal
        open={showCompareCyclesHelp}
        onCancel={() => setShowCompareCyclesHelp(false)}
        footer={null}
        title={t('symptomTracking.modules.compareCyclesHelp.title')}
      >
        <p>
          <Trans i18nKey="symptomTracking.modules.compareCyclesHelp.description" components={{ br: <br /> }} />
        </p>
      </Modal>
    </div>
  );
}
